import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { app } from '@/auth/firebase';
import { apiService } from '@/api';

/**
 * VAPID key for Web Push notifications (public key from Firebase Console).
 * Generated from: Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
 */
const VAPID_KEY = 'BMOQits3mxsOT9nO8wbHW8VwrF1KV-MlX_diFqutOaCvgP_HDOfiGUZI19dAIG7NNjPyeYeAXhXYguwzBje9GQ8';

const PERMISSION_ASKED_KEY = 'vivideo_notification_asked';
const FCM_TOKEN_KEY = 'vivideo_fcm_token';

// Holds the unsubscribe function returned by onMessage so repeat calls to
// setupForegroundHandler can never stack multiple listeners (which would
// cause the same push to fire `new Notification(...)` more than once).
let _foregroundUnsubscribe: (() => void) | null = null;

export const notificationService = {
  /**
   * Check if push notifications are supported in the current browser
   */
  async isSupported(): Promise<boolean> {
    console.log('[Notifications] Checking support...');
    
    if (!('Notification' in window)) {
      console.log('[Notifications] ❌ Notification API not supported');
      return false;
    }
    console.log('[Notifications] ✓ Notification API supported');
    
    if (!('serviceWorker' in navigator)) {
      console.log('[Notifications] ❌ Service Worker not supported');
      return false;
    }
    console.log('[Notifications] ✓ Service Worker supported');
    
    // Check Firebase Messaging support
    try {
      const fcmSupported = await isSupported();
      if (!fcmSupported) {
        console.log('[Notifications] ❌ Firebase Messaging not supported (isSupported returned false)');
        return false;
      }
      console.log('[Notifications] ✓ Firebase Messaging supported');
    } catch (err) {
      console.log('[Notifications] ❌ Firebase Messaging check failed:', err);
      return false;
    }
    
    return true;
  },

  /**
   * Check if we've already asked for permission
   */
  hasAskedPermission(): boolean {
    return localStorage.getItem(PERMISSION_ASKED_KEY) === 'true';
  },

  /**
   * Get the current permission status
   */
  getPermissionStatus(): NotificationPermission | 'unsupported' {
    if (!('Notification' in window)) {
      return 'unsupported';
    }
    return Notification.permission;
  },

  /**
   * Request notification permission and register FCM token
   * Call this when user starts their first video generation
   */
  async requestPermissionAndRegister(userId: string): Promise<boolean> {
    console.log('[Notifications] requestPermissionAndRegister called with userId:', userId);
    
    try {
      // Check if VAPID key is configured
      if (!VAPID_KEY || VAPID_KEY.length < 50) {
        console.warn('[Notifications] VAPID key not configured. Please set it in notificationService.ts');
        console.warn('[Notifications] Get it from: https://console.firebase.google.com/project/vivideo-ai/settings/cloudmessaging');
        return false;
      }
      console.log('[Notifications] VAPID key is configured');

      // Check if already asked
      if (this.hasAskedPermission()) {
        console.log('[Notifications] Already asked for permission before');
        return false;
      }
      console.log('[Notifications] Has not asked permission before');

      // Check browser support
      console.log('[Notifications] Checking browser support...');
      const supported = await this.isSupported();
      if (!supported) {
        console.log('[Notifications] Push notifications not supported');
        return false;
      }
      console.log('[Notifications] Browser supports push notifications');

      // Mark that we've asked (even if user denies, don't ask again)
      localStorage.setItem(PERMISSION_ASKED_KEY, 'true');

      // Request permission
      console.log('[Notifications] Requesting permission from browser...');
      const permission = await Notification.requestPermission();
      console.log('[Notifications] Permission result:', permission);
      
      if (permission !== 'granted') {
        console.log('[Notifications] Permission denied');
        return false;
      }

      console.log('[Notifications] Permission granted, getting FCM token...');

      // Register service worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('[Notifications] Service worker registered:', registration);

      // Get FCM token
      const messaging = getMessaging(app);
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (!token) {
        console.error('[Notifications] Failed to get FCM token');
        return false;
      }

      console.log('[Notifications] FCM token obtained:', token.substring(0, 20) + '...');

      // Save token locally for reference
      localStorage.setItem(FCM_TOKEN_KEY, token);

      // Send token to backend
      await apiService.updateFcmToken(userId, token);
      console.log('[Notifications] FCM token saved to backend');

      // Set up foreground handler
      this.setupForegroundHandler();

      return true;
    } catch (error) {
      console.error('[Notifications] Error during registration:', error);
      return false;
    }
  },

  /**
   * Re-register FCM token (call on login if permission was already granted)
   */
  async reRegisterIfNeeded(userId: string): Promise<void> {
    try {
      // Check if VAPID key is configured
      if (!VAPID_KEY || VAPID_KEY.length < 50) {
        return;
      }

      // Only re-register if permission was previously granted. iOS Safari (outside
      // an installed web app) and some in-app browsers have no Notification API at
      // all — reading Notification.permission there throws a ReferenceError.
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
        return;
      }

      const supported = await this.isSupported();
      if (!supported) {
        return;
      }

      console.log('[Notifications] Re-registering FCM token...');

      // Register service worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

      // Get FCM token
      const messaging = getMessaging(app);
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        const existingToken = localStorage.getItem(FCM_TOKEN_KEY);
        
        // Only update if token changed
        if (token !== existingToken) {
          console.log('[Notifications] Token changed, updating backend...');
          localStorage.setItem(FCM_TOKEN_KEY, token);
          await apiService.updateFcmToken(userId, token);
        }

        // Set up foreground handler
        this.setupForegroundHandler();
      }
    } catch (error) {
      console.error('[Notifications] Error during re-registration:', error);
    }
  },

  /**
   * Set up handler for foreground messages (when app is visible).
   *
   * Idempotent: if called more than once in the same tab (e.g. from both
   * requestPermissionAndRegister and reRegisterIfNeeded), the previous
   * onMessage subscription is removed first so only one listener is active
   * at a time. Without this, each call stacks a new listener and a single
   * push triggers `new Notification(...)` multiple times.
   */
  setupForegroundHandler(): void {
    try {
      if (_foregroundUnsubscribe) {
        try {
          _foregroundUnsubscribe();
        } catch (e) {
          console.warn('[Notifications] Error unsubscribing previous foreground handler:', e);
        }
        _foregroundUnsubscribe = null;
      }

      const messaging = getMessaging(app);

      _foregroundUnsubscribe = onMessage(messaging, (payload) => {
        console.log('[Notifications] Foreground message received:', payload);

        const title = payload.notification?.title || 'Video Ready!';
        const body = payload.notification?.body || 'Your video is ready to view';

        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          new Notification(title, {
            body,
            icon: '/logo.svg',
            tag: 'vivideo-foreground',
          });
        }
      });

      console.log('[Notifications] Foreground handler set up');
    } catch (error) {
      console.error('[Notifications] Error setting up foreground handler:', error);
    }
  },
};

export default notificationService;

